import React from 'react'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import { Props as IntlProps } from 'react-intl/src/components/message'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { CsCheckIcon } from '../../legacy-design-system/icons/check-icon'
import {
  formLabelCss,
  panelTitleCss,
  readOnlyGridCss
} from '../../legacy-design-system/text'
export type ItemStatus = 'COMPLETED' | 'ACTIVE' | 'INACTIVE'

export interface ItemProps {
  readonly title: MessageDescriptor
  readonly description: IntlProps
  readonly completedMessage: MessageDescriptor
  readonly status: ItemStatus
  readonly onClose?: () => void
}

const mainContainerCss = css`
  display: flex;
  flex-direction: row;
  min-height: 50px;
  padding: 25px 20px;
  text-align: left;
  background-color: ${theme.color.white};
  border-bottom: 1px solid ${theme.color.neutral30};
  :last-child {
    border-bottom: 0px solid transparent;
    border-radius: 5px;
  }
`

const hoverContainerCss = cx(
  mainContainerCss,
  css`
    &:hover {
      background-color: ${theme.color.teal10};
    }
    cursor: pointer;
  `
)

const disabledContainerCss = cx(
  mainContainerCss,
  css`
    pointer-events: none;
  `
)

const completedContainerCss = cx(
  disabledContainerCss,
  css`
    background-image: linear-gradient(
      180deg,
      ${theme.color.white} 0.5%,
      ${theme.color.neutral10} 24.5%,
      ${theme.color.neutral10} 75%
    );
  `
)

const imageContainerCss = css`
  display: flex;
  justify-content: center;
  margin-top: 2px;
  margin-left: 4px;
`

const parentContentContainerCss = css`
  display: flex;
  flex-direction: column;
  margin-left: 12px;
`

const titleCss = css`
  display: inline;
`

const completedTitleCss = css`
  color: ${theme.color.neutral50};
`

const enabledTitleCss = css`
  color: ${theme.color.neutral100};
`

const disabledTitleCss = css`
  color: ${theme.color.neutral40};
`

const enabledSubtitleCss = css`
  a {
    text-decoration: none;
  }
`

const disabledSubtitleCss = css`
  color: ${theme.color.neutral30};
  a {
    opacity: 0.2;
  }
`

const completedSubtitleCss = css`
  color: ${theme.color.neutral50};
  opacity: 0.8;
  a {
    pointer-events: auto;
  }
`

const enabledIconCss = css`
  color: ${theme.color.neutral40};
`

const disabledIconCss = css`
  color: ${theme.color.neutral20};
`

const completedIconCss = css`
  color: ${theme.color.green50};
`

const completedMessageCss = css`
  color: ${theme.color.green50};
  margin-left: 10px;
`

export function OnboardingItem({
  title,
  description,
  completedMessage,
  status
}: ItemProps) {
  const isEnabled = status === 'ACTIVE'
  const isCompleted = status === 'COMPLETED'

  return (
    <div
      className={
        isCompleted
          ? completedContainerCss
          : isEnabled
          ? hoverContainerCss
          : disabledContainerCss
      }
    >
      <div className={imageContainerCss}>
        <CsCheckIcon
          size={18}
          className={
            isCompleted
              ? completedIconCss
              : isEnabled
              ? enabledIconCss
              : disabledIconCss
          }
        />
      </div>
      <div className={parentContentContainerCss}>
        <span>
          <p
            className={cx(
              formLabelCss,
              titleCss,
              isCompleted
                ? completedTitleCss
                : isEnabled
                ? enabledTitleCss
                : disabledTitleCss
            )}
            children={<FormattedMessage {...title} />}
          />
          {isCompleted && (
            <FormattedMessage {...completedMessage}>
              {(text) => (
                <span
                  className={cx(panelTitleCss, completedMessageCss)}
                  children={text}
                />
              )}
            </FormattedMessage>
          )}
        </span>
        <span
          className={cx(
            readOnlyGridCss,
            isCompleted
              ? completedSubtitleCss
              : isEnabled
              ? enabledSubtitleCss
              : disabledSubtitleCss
          )}
          children={<FormattedMessage {...description} />}
        />
      </div>
    </div>
  )
}
